import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const AdminTestimonials = () => {
  const [testimonials, setTestimonials] = useState<any[]>([]);
  const [edits, setEdits] = useState<Record<string, any>>({});
  const [saving, setSaving] = useState<string | null>(null);

  const load = async () => {
    const { data } = await supabase.from("testimonials").select("*").order("slot", { ascending: true });
    setTestimonials(data || []);
    const e: Record<string, any> = {};
    data?.forEach((t) => {
      e[t.id] = { name: t.name, location: t.location, text: t.text, rating: t.rating };
    });
    setEdits(e);
  };

  useEffect(() => { load(); }, []);

  const save = async (t: any) => {
    setSaving(t.id);
    const edit = edits[t.id];
    const { error } = await supabase.from("testimonials").update({
      name: edit.name,
      location: edit.location,
      text: edit.text,
      rating: Number(edit.rating) || 5,
    }).eq("id", t.id);
    if (error) toast.error("Erreur: " + error.message);
    else toast.success("Témoignage mis à jour !");
    setSaving(null);
  };

  const addNew = async () => {
    const maxSlot = testimonials.reduce((max, t) => Math.max(max, t.slot), 0);
    const { error } = await supabase.from("testimonials").insert({
      slot: maxSlot + 1,
      name: "Nouveau client",
      location: "Ville, Pays",
      text: "Témoignage...",
      rating: 5,
    });
    if (error) toast.error("Erreur: " + error.message);
    else { toast.success("Témoignage ajouté !"); load(); }
  };

  const remove = async (id: string) => {
    const { error } = await supabase.from("testimonials").delete().eq("id", id);
    if (error) toast.error("Erreur: " + error.message);
    else { toast.success("Témoignage supprimé !"); load(); }
  };

  const updateField = (id: string, field: string, value: string) => {
    setEdits((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={addNew} size="sm"><Plus className="w-4 h-4 mr-1" /> Ajouter un témoignage</Button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {testimonials.map((t) => (
          <Card key={t.id} className="border-border">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-sm">#{t.slot}</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => remove(t.id)}>
                <Trash2 className="w-4 h-4 text-destructive" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-2">
              <div>
                <label className="block font-body text-xs text-muted-foreground mb-1">Nom</label>
                <Input value={edits[t.id]?.name || ""} onChange={(e) => updateField(t.id, "name", e.target.value)} />
              </div>
              <div>
                <label className="block font-body text-xs text-muted-foreground mb-1">Lieu</label>
                <Input value={edits[t.id]?.location || ""} onChange={(e) => updateField(t.id, "location", e.target.value)} />
              </div>
              <div>
                <label className="block font-body text-xs text-muted-foreground mb-1">Texte</label>
                <Textarea value={edits[t.id]?.text || ""} onChange={(e) => updateField(t.id, "text", e.target.value)} rows={3} />
              </div>
              <div>
                <label className="block font-body text-xs text-muted-foreground mb-1">Note (1-5)</label>
                <Input type="number" min={1} max={5} value={edits[t.id]?.rating ?? 5} onChange={(e) => updateField(t.id, "rating", e.target.value)} />
              </div>
              <Button onClick={() => save(t)} disabled={saving === t.id} className="w-full" size="sm">
                {saving === t.id ? "Enregistrement..." : "Enregistrer"}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminTestimonials;
